import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart, Share2, ChevronRight, ChevronLeft, ChevronDown, ChevronUp,
  Star, ShoppingCart, Truck, Shield, RotateCcw, Check, Minus, Plus,
  Package, ArrowRight, X, Calendar, User, Zap,
} from "lucide-react";

import { AppDispatch, RootState } from "../../redux/store";
import { listenToProductData, listenToPublishedProducts, Product } from "../../redux/productSlice";
import { addToCart, decrementCartItem } from "../../redux/cartSlice";
import { fetchProductReviewsByListing } from "../../redux/productRatingSlice";
import { listenToCommerceData } from "../../redux/commercePartnersSlice";
import { CurrencyContext } from "../../GLOBAL/CurrencyContext";
import { electronicsAttributes, ElectronicsAttributeGroup, electronicsColorAttributes, electronicsSizeAttributes } from "../../UNIVERSAL/ATTRIBUTES/ElectronicsAttributes";
import { productColors, GENERAL_COLOR_ATTRIBUTE_ID } from "../../UNIVERSAL/ATTRIBUTES/ProductsAttributes";





/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS — white bg, purple/gradient accents
═══════════════════════════════════════════════════════════ */
const T = {
  bg:         "#FFFFFF",
  bgSoft:     "#FAF9FF",
  bgMuted:    "#F0EDF8",
  ink:        "#0A0A0A",
  inkMid:     "#1C1C1C",
  border:     "rgba(106,0,255,0.15)",
  borderDark: "rgba(106,0,255,0.35)",
  muted:      "#8B82A8",
  mutedSoft:  "#C4BBEE",
  blue:       "#007BFF",
  purple:     "#6A00FF",
  pink:       "#FF00D4",
  gold:       "#B8860B",
  red:        "#CC2200",
  green:      "#15803D",
  grad:       "linear-gradient(90deg,#007BFF 0%,#6A00FF 50%,#FF00D4 100%)",
  gradAnim:   "linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4,#6A00FF,#007BFF)",
  offwhite:   "#0A0A0A",   /* replaces dark-mode offwhite — use ink on white */
  blackSoft:  "#3D3A4A",   /* replaces dark-mode blackSoft */
};

const GRAD = T.grad;
const GA   = T.gradAnim;

/* ── Saloisa-style colour helpers ── */
function sphere3D(hex: string): string {
  return [
    `radial-gradient(circle at 35% 32%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.10) 28%, transparent 52%),`,
    `radial-gradient(circle at 68% 72%, rgba(0,0,0,0.30) 0%, transparent 55%),`,
    `radial-gradient(circle at 50% 50%, ${hex} 0%, ${hex} 100%)`,
  ].join(" ");
}

const getColorAttr = (id?: number) => {
  if (!id) return null;
  const grp = productColors.find(g => g.id === GENERAL_COLOR_ATTRIBUTE_ID);
  return grp?.values.find(v => v.id === id) ?? null;
};

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  ::-webkit-scrollbar{display:none;}
  button{font-family:inherit;}
  @keyframes pdSpin{to{transform:rotate(360deg);}}
  @keyframes gradSpin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}

  .sw{width:30px;height:30px;border-radius:50%;cursor:pointer;border:2px solid rgba(106,0,255,.15);transition:all .15s;flex-shrink:0;}
  .sw:hover{transform:scale(1.12);}
  .sw.on{border:3px solid #6A00FF;box-shadow:0 0 0 2px #FFFFFF,0 0 0 4px #6A00FF;transform:scale(1.2);}

  .sz{padding:8px 18px;border-radius:999px;border:1.5px solid rgba(106,0,255,.25);background:transparent;color:#8B82A8;font-size:12px;font-weight:700;cursor:pointer;transition:all .15s;}
  .sz:hover{border-color:rgba(106,0,255,.6);color:#0A0A0A;}
  .sz.on{background:linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4);background-size:200% 100%;animation:gradSpin 3s ease infinite;border-color:transparent;color:#fff;box-shadow:0 2px 12px rgba(106,0,255,.35);}

  .ac{padding:6px 14px;border-radius:999px;font-size:11px;font-weight:700;border:1.5px solid rgba(106,0,255,.2);background:rgba(106,0,255,.06);color:#8B82A8;cursor:default;}

  .th{border-radius:10px;overflow:hidden;cursor:pointer;border:1.5px solid rgba(106,0,255,.15);transition:border-color .15s;}
  .th:hover{border-color:rgba(106,0,255,.5);}
  .th.on{border-color:#6A00FF;box-shadow:0 0 0 1px #6A00FF;}

  .ab{flex:1;padding:14px 0;border-radius:999px;font-size:11px;font-weight:800;letter-spacing:.16em;cursor:pointer;transition:all .2s;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:7px;}
  .ab.outline{background:transparent;border:1.5px solid rgba(106,0,255,.4);color:#0A0A0A;}
  .ab.outline:hover{background:rgba(106,0,255,.07);border-color:rgba(106,0,255,.7);}
  .ab.fill{background:linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4);background-size:200% 100%;animation:gradSpin 3s ease infinite;border:none;color:#fff;box-shadow:0 6px 24px rgba(106,0,255,.35);}
  .ab.fill:hover{transform:translateY(-1px);box-shadow:0 10px 32px rgba(106,0,255,.50);}
  .ab:disabled{background:rgba(106,0,255,.06)!important;color:#C4BBEE!important;border-color:transparent!important;cursor:not-allowed!important;animation:none!important;box-shadow:none!important;}

  .ym-card{border:1.5px solid rgba(106,0,255,.15);overflow:hidden;background:#fff;cursor:pointer;transition:box-shadow .18s,transform .18s,border-color .18s;}
  .ym-card:hover{box-shadow:0 4px 18px rgba(106,0,255,.12);transform:translateY(-2px);border-color:rgba(106,0,255,.4);}
`;

/* ─── helpers ─── */
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" });

const getInitials = (name?: string) =>
  name ? name.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase() : "?";

/* ─── Stars ─── */
const Stars: React.FC<{ rating:number; size?:number; count?:number }> = ({ rating, size=13, count }) => (
  <div style={{ display:"flex", alignItems:"center", gap:3 }}>
    {[1,2,3,4,5].map(i=>(
      <span key={i} style={{ position:"relative", display:"inline-block", width:size, height:size }}>
        <Star size={size} color={T.mutedSoft} fill={T.mutedSoft}/>
        {i<=Math.floor(rating)
          ? <span style={{ position:"absolute", inset:0 }}><Star size={size} color={T.gold} fill={T.gold}/></span>
          : i-1<rating&&rating<i
          ? <span style={{ position:"absolute", inset:0, overflow:"hidden", width:`${(rating-(i-1))*100}%` }}><Star size={size} color={T.gold} fill={T.gold}/></span>
          : null
        }
      </span>
    ))}
    {count!==undefined&&<span style={{ fontSize:11, color:T.muted, marginLeft:4 }}>({count}+)</span>}
  </div>
);

/* ─── Rating bar ─── */
const RatingBar: React.FC<{ label:string; value:number; total?:number }> = ({ label, value, total }) => {
  const pct = total&&total>0 ? Math.round((value/total)*100) : Math.round((value/5)*100);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
      <span style={{ fontSize:11, color:T.muted, width:80, flexShrink:0 }}>{label}</span>
      <div style={{ flex:1, height:4, background:T.bgMuted, overflow:"hidden", borderRadius:2 }}>
        <div style={{ width:`${pct}%`, height:"100%", background:GRAD, transition:"width .6s", borderRadius:2 }}/>
      </div>
      <span style={{ fontSize:11, fontWeight:700, color:T.ink, width:28, textAlign:"right" }}>
        {typeof total==="number"?`${pct}%`:value.toFixed(1)}
      </span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const ProductDetails: React.FC = () => {
  const { listingId: paramId } = useParams<{ listingId:string }>();
  const navigate  = useNavigate();
  const dispatch  = useDispatch<AppDispatch>();
  const { rate: fxRate, symbol } = useContext(CurrencyContext);

  const authUser       = useSelector((s:RootState) => s.auth.user);
  const userId         = authUser?.userId;
  const allUsers       = useSelector((s:RootState) => s.auth.allUsers);
  const cartItems      = useSelector((s:RootState) => s.cart.items);
  const product        = useSelector((s:RootState) => s.products.currentProduct);
  const allProducts    = useSelector((s:RootState) => s.products.products ?? []);
  const reviews        = useSelector((s:RootState) => paramId ? s.productRatings.reviews[paramId] ?? [] : []);
  const currentPartner = useSelector((s:RootState) => s.commercePartners.currentPartner);

  const [activeId,   setActiveId]   = useState(paramId ?? "");
  const [selColor,   setSelColor]   = useState<number|null>(null);
  const [selSize,    setSelSize]    = useState<number|null>(null);
  const [qty,        setQty]        = useState(1);
  const [imgIndex,   setImgIndex]   = useState(0);
  const [modalIndex, setModalIndex] = useState<number|null>(null);
  const [wished,     setWished]     = useState(false);
  const [isMobile,   setIsMobile]   = useState(window.innerWidth <= 768);

  useEffect(()=>{ const fn=()=>setIsMobile(window.innerWidth<=768); window.addEventListener("resize",fn); return()=>window.removeEventListener("resize",fn); },[]);

  useEffect(()=>{ dispatch(listenToPublishedProducts()); },[dispatch]);

  useEffect(()=>{
    if(!activeId)return;
    dispatch(listenToProductData(activeId));
    dispatch(fetchProductReviewsByListing(activeId));
    setSelColor(null); setSelSize(null); setQty(1); setImgIndex(0);
    window.scrollTo({ top:0, behavior:"smooth" });
  },[activeId, dispatch]);

  useEffect(()=>{ if((product as any)?.brandId) dispatch(listenToCommerceData((product as any).brandId)); },[product, dispatch]);

  /* ── variants ── */
  const variantMode = useMemo(()=>{
    if(!product?.variants||Object.keys(product.variants).length===0) return null;
    const hasSizes = Object.values(product.variants).some((v:any)=>Array.isArray(v.sizeIds)&&v.sizeIds.length>0);
    if(hasSizes) return "fashion";
    const hasBeauty = Object.values(product.variants).some((v:any)=>typeof v.imageUrl==="string");
    if(hasBeauty) return "beauty";
    return null;
  },[product]);

  const colourIds   = variantMode ? Object.keys(product?.variants??{}).map(Number) : [];
  const hasVariants = colourIds.length>0;
  const sizeIds: number[] = selColor ? (product?.variants?.[selColor]?.sizeIds??[]) : [];
  const variantImg  = selColor ? (product?.variants?.[selColor]?.imageUrl??null) : null;

  /* ── images ── */
  const photos: string[] = useMemo(()=>[
    ...(variantImg?[variantImg]:[]),
    ...(product?.photos??[]).filter(p=>p!==variantImg),
  ],[product,variantImg]);

  const media = useMemo(()=>[
    ...(product?.photos??[]).map(url=>({type:"image" as const, url})),
    ...(product?.videoUrl?[{type:"video" as const, url:product.videoUrl}]:[]),
  ],[product]);

  /* ── electronics specs ── */
  const electronicsSpecs = useMemo(()=>{
    if(!(product as any)?.attributes) return [];
    return Object.entries((product as any).attributes).map(([gid,vid])=>{
      const group = electronicsAttributes.find(g=>g.id===Number(gid));
      if(!group) return null;
      if(Array.isArray(vid)){
        const values = group.values.filter(v=>(vid as number[]).includes(v.id));
        return {group,values};
      }
      if(typeof vid==="number"){
        const value = group.values.find(v=>v.id===vid);
        if(!value) return null;
        return {group, values:[value]};
      }
      return null;
    }).filter(Boolean) as {group:ElectronicsAttributeGroup; values:{id:number;name:string}[]}[];
  },[product]);

  /* ── pricing ── */
  const fx   = fxRate??1;
  const sym  = symbol??"€";
  const price = product ? `${sym}${((product.rate??0)*fx).toFixed(2)}` : "";
  const origPrice = (product as any)?.compareAtRate ? `${sym}${((product as any).compareAtRate*fx).toFixed(2)}` : null;
  const discount  = (product as any)?.compareAtRate&&(product as any).compareAtRate>(product?.rate??0)
    ? Math.round((((product as any).compareAtRate-(product!.rate!))/(product as any).compareAtRate)*100) : null;

  /* ── cart ── */
  const cartQty   = cartItems.filter(i=>i.listingId===activeId).reduce((s,i)=>s+i.qty,0);
  const totalQty  = cartItems.reduce((s,i)=>s+i.qty,0);
  const totalPrice= cartItems.reduce((s,i)=>s+i.qty*i.price,0);

  const canBuy = !hasVariants
    ||(variantMode==="fashion"&&selColor!==null&&(sizeIds.length===0||selSize!==null))
    ||(variantMode==="beauty"&&selColor!==null);

  const hostUser = useMemo(()=>(allUsers as any[]).find(u=>u.userId===product?.hostId)??null,[allUsers,product?.hostId]);
  const brand    = currentPartner;

  const getColorName = (id:number) => electronicsColorAttributes?.values?.find((v:any)=>v.id===id)?.name??String(id);
  const getSizeName  = (id:number) => electronicsSizeAttributes?.values?.find((v:any)=>v.id===id)?.name??String(id);

  /* ── You Might Also Like — same logic as Saloisa ── */
  const suggestedProducts = useMemo(()=>{
    const cartIds    = new Set(cartItems.map((i:any)=>i.listingId));
    const cartCatIds = new Set(cartItems.map((i:any)=>{ const l=(allProducts as any[]).find((l:any)=>l.listingId===i.listingId); return l?.categoryId??null; }).filter(Boolean));
    const eligible   = (allProducts as any[]).filter((p:any)=>p.isPublished&&!cartIds.has(p.listingId)&&p.photos?.[0]&&p.listingId!==activeId);
    const related    = eligible.filter((p:any)=>cartCatIds.has(p.categoryId));
    const pool       = related.length>=8?related:[...related,...eligible.filter((p:any)=>!cartCatIds.has(p.categoryId))];
    return [...pool].sort(()=>Math.random()-0.5).slice(0,10);
  },[allProducts,cartItems,activeId]);

  const handleAddToCart = () => {
    if(!product||!canBuy) return;
    dispatch(addToCart({
      listingId: product.listingId,
      name:      product.name??"",
      price:     product.rate??0,
      qty,
      photo:     variantImg??photos[0],
      colorId:   selColor??undefined,
      sizeId:    selSize??undefined,
      hostId:    product.hostId,
      brandId:   (product as any).brandId,
    }));
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate(`/ProductCheckout/${userId}`, { state:{ userId, totalQty, totalPrice, items:cartItems } });
  };

  /* ── loading ── */
  if(!product) return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{CSS}</style>
      <div style={{ width:24, height:24, border:`2px solid rgba(106,0,255,.2)`, borderTopColor:T.purple, borderRadius:"50%", animation:"pdSpin 1s linear infinite" }}/>
    </div>
  );

  /* ════════════════ REVIEWS SECTION ════════════════ */
  const ReviewsSection: React.FC<{mob?:boolean}> = ({mob}) => {
    const [showAll,setShowAll]=useState(false);
    const displayed = showAll?reviews:reviews.slice(0,3);
    const avg = reviews.length ? reviews.reduce((s:number,r:any)=>s+r.rating,0)/reviews.length : 0;

    return (
      <section style={{ padding:mob?"0 16px 24px":"0 0 32px" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, paddingTop:16, borderTop:`1px solid ${T.border}` }}>
          <h3 style={{ fontSize:mob?18:22, fontWeight:800, color:T.ink, letterSpacing:-0.3 }}>
            Reviews {reviews.length>0?`(${reviews.length})`:""}
          </h3>
          {reviews.length>3&&(
            <button onClick={()=>setShowAll(v=>!v)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, color:T.muted, display:"flex", alignItems:"center", gap:3 }}>
              {showAll?"Show less":"View all"} <ArrowRight size={11}/>
            </button>
          )}
        </div>

        {reviews.length>0&&(
          <div style={{ background:T.bgSoft, border:`1px solid ${T.border}`, padding:"16px 18px", marginBottom:16, borderRadius:16 }}>
            <div style={{ display:"flex", gap:24, flexWrap:"wrap" }}>
              <div style={{ textAlign:"center", minWidth:80 }}>
                <div style={{ fontSize:44, fontWeight:900, color:T.ink, lineHeight:1, background:GRAD, backgroundSize:"200% 100%", animation:"gradSpin 3s ease infinite", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>
                  {avg.toFixed(1)}
                </div>
                <Stars rating={avg} size={14}/>
                <div style={{ fontSize:10, color:T.muted, marginTop:4 }}>{reviews.length} reviews</div>
              </div>
              <div style={{ flex:1, minWidth:160 }}>
                {[5,4,3,2,1].map(star=>{
                  const cnt=(reviews as any[]).filter(r=>Math.round(r.rating)===star).length;
                  return <RatingBar key={star} label={`${star} star${star>1?"s":""}`} value={cnt} total={reviews.length}/>;
                })}
              </div>
            </div>
          </div>
        )}

        {(displayed as any[]).map((rev:any,i:number)=>{
          const user=(allUsers as any[]).find(u=>u.userId===rev.guestId);
          return (
            <motion.div key={i} initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{delay:i*.06}}
              style={{ paddingBottom:16, marginBottom:16, borderBottom:`1px solid ${T.border}` }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                <div style={{ width:32, height:32, borderRadius:"50%", background:T.bgMuted, overflow:"hidden", flexShrink:0, border:`1px solid ${T.border}` }}>
                  {user?.profilePicture
                    ? <img src={user.profilePicture} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                    : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:T.purple }}>
                        {(user?.firstName?.[0]??"U").toUpperCase()}
                      </div>
                  }
                </div>
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:T.ink }}>{user?.firstName??"Verified Buyer"}</div>
                  <div style={{ fontSize:10, color:T.muted }}>{fmtDate(rev.dateCreated)}</div>
                </div>
                <div style={{ marginLeft:"auto" }}><Stars rating={rev.rating} size={12}/></div>
              </div>
              {rev.title&&<div style={{ fontSize:13, fontWeight:700, color:T.ink, marginBottom:4 }}>{rev.title}</div>}
              <div style={{ fontSize:13, color:T.muted, lineHeight:1.7 }}>{rev.reviewText}</div>
              {Array.isArray(rev.images)&&rev.images.length>0&&(
                <div style={{ display:"flex", gap:8, marginTop:8 }}>
                  {rev.images.map((img:string,j:number)=>(
                    <img key={j} src={img} alt="" style={{ width:64, height:64, objectFit:"cover", border:`1px solid ${T.border}`, borderRadius:10 }}/>
                  ))}
                </div>
              )}
            </motion.div>
          );
        })}

        {reviews.length===0&&(
          <div style={{ padding:"24px 0", textAlign:"center" }}>
            <Star size={28} color={T.mutedSoft} style={{ marginBottom:8 }}/>
            <p style={{ fontSize:12, color:T.muted }}>No reviews yet — be the first</p>
          </div>
        )}
      </section>
    );
  };

  /* ════════════════ BRAND & SELLER ════════════════
  
    const BrandSellerSection: React.FC<{mob?:boolean}> = ({mob}) => (
    <section style={{ padding:mob?"0 16px 24px":"0 0 32px" }}>
      <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:16, marginBottom:16 }}>
        <h3 style={{ fontSize:mob?18:22, fontWeight:800, color:T.ink }}>About the Seller</h3>
      </div>
      <div style={{ display:"flex", gap:mob?0:24, flexDirection:mob?"column":"row", alignItems:mob?"stretch":"flex-start" }}>
        {brand&&(
          <div style={{ flex:1, background:T.bgSoft, border:`1px solid ${T.border}`, borderRadius:16, padding:"18px", marginBottom:mob?12:0 }}>
            <div style={{ display:"flex", gap:12, alignItems:"center", marginBottom:12 }}>
              <div style={{ width:48, height:48, borderRadius:12, background:T.bgMuted, overflow:"hidden", flexShrink:0, border:`1px solid ${T.border}` }}>
                {(brand as any).image
                  ? <img src={(brand as any).image} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                  : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:700, color:T.purple }}>{getInitials((brand as any).name)}</div>
                }
              </div>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:T.ink, marginBottom:2 }}>{(brand as any).name}</div>
                <div style={{ fontSize:9, fontWeight:800, letterSpacing:"0.14em", background:GRAD, backgroundSize:"200% 100%", animation:"gradSpin 3s ease infinite", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>OFFICIAL BRAND</div>
              </div>
            </div>
            {(brand as any).joinDate&&<div style={{ fontSize:11, color:T.muted, display:"flex", alignItems:"center", gap:5 }}><Calendar size={11}/> Partnered {fmtDate((brand as any).joinDate)}</div>}
          </div>
        )}
        {hostUser&&(
          <div style={{ flex:1, background:T.bgSoft, border:`1px solid ${T.border}`, borderRadius:16, padding:"18px" }}>
            <div style={{ display:"flex", gap:12, alignItems:"center", marginBottom:12 }}>
              <div style={{ width:48, height:48, borderRadius:"50%", background:T.bgMuted, overflow:"hidden", flexShrink:0, border:`1px solid ${T.border}` }}>
                {hostUser.profilePicture
                  ? <img src={hostUser.profilePicture} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                  : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:700, color:T.purple }}>{(hostUser.firstName?.[0]??"U").toUpperCase()}</div>
                }
              </div>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:T.ink, marginBottom:2 }}>{hostUser.firstName}</div>
                <div style={{ fontSize:9, color:T.muted, letterSpacing:"0.12em" }}>SOLD BY</div>
              </div>
            </div>
            <div style={{ fontSize:11, color:T.muted, display:"flex", flexDirection:"column", gap:4 }}>
              {hostUser.joinDate&&<span style={{ display:"flex", alignItems:"center", gap:5 }}><Calendar size={11}/> Joined {fmtDate(hostUser.joinDate)}</span>}
              {hostUser.address?.country&&<span style={{ display:"flex", alignItems:"center", gap:5 }}><Package size={11}/> {hostUser.address.country}</span>}
            </div>
            <button onClick={()=>authUser&&hostUser?navigate(`/messages/${hostUser.userId}`):navigate("/AuthSwitch")}
              style={{ marginTop:14, width:"100%", padding:"10px 0", background:"none", border:`1.5px solid rgba(106,0,255,.4)`, color:T.purple, borderRadius:999, cursor:"pointer", fontSize:11, fontWeight:700, letterSpacing:"0.12em", transition:"all .2s" }}>
              MESSAGE SELLER
            </button>
          </div>
        )}
      </div>
    </section>
  );
  
  
  */

  /* ── selectors ── */
  const ColourSelector = () => colourIds.length>0 ? (
    <div style={{ marginBottom:20 }}>
      <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.16em", color:T.muted, marginBottom:10 }}>
        COLOUR: <span style={{ fontWeight:500, color:T.ink }}>{selColor?(getColorAttr(selColor)?.name??getColorName(selColor)):"—"}</span>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(52px,1fr))", gap:10 }}>
        {colourIds.map(cId=>{
          const col      = getColorAttr(cId);
          const elCol    = electronicsColorAttributes?.values?.find((v:any)=>v.id===cId);
          const hex      = col?.hexCodes?.[0] ?? elCol?.hexCodes?.[0];
          const isSel    = selColor===cId;
          const isMulti  = col?.name==="Multicolor";
          const isTransp = col?.name==="Transparent";
          return (
            <button key={cId} onClick={()=>{setSelColor(cId);setSelSize(null);setImgIndex(0);}}
              style={{ background:"none", border:"none", padding:0, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:5 }}>
              <div style={{ position:"relative", width:34, height:34 }}>
                {isMulti
                  ? <div style={{ width:34, height:34, borderRadius:"50%", background:"conic-gradient(#e53935 0deg 51deg,#FF9800 51deg 102deg,#FFEB3B 102deg 153deg,#228B22 153deg 204deg,#4169E1 204deg 255deg,#9C27B0 255deg 306deg,#e53935 306deg 360deg)", outline:isSel?"2.5px solid #6A00FF":"none", outlineOffset:3 }}/>
                  : isTransp
                  ? <div style={{ width:34, height:34, borderRadius:"50%", background:"repeating-conic-gradient(#d0d0d0 0% 25%,#f8f8f8 0% 50%) 0 0/10px 10px", border:"1.5px solid rgba(0,0,0,0.10)", outline:isSel?"2.5px solid #6A00FF":"none", outlineOffset:3 }}/>
                  : <div style={{ width:34, height:34, borderRadius:"50%",
                      background: hex ? sphere3D(hex) : sphere3D("#CCCCCC"),
                      boxShadow:`inset -4px -3px 8px rgba(0,0,0,.18),inset 3px 3px 7px rgba(255,255,255,.25),0 2px 8px rgba(0,0,0,.10)${isSel?",0 0 0 2.5px #6A00FF":""}`,
                      outline:isSel?"2.5px solid #6A00FF":"none", outlineOffset:3, transition:"transform .18s" }}/>
                }
                {isSel&&<div style={{ position:"absolute", bottom:-3, right:-3, width:14, height:14, borderRadius:"50%", background:T.purple, display:"flex", alignItems:"center", justifyContent:"center" }}><Check size={8} color="#fff" strokeWidth={3}/></div>}
              </div>
              <span style={{ fontSize:9, fontWeight:isSel?600:400, color:isSel?T.ink:T.muted, textAlign:"center", maxWidth:52, lineHeight:1.2 }}>{col?.name??elCol?.name??String(cId)}</span>
            </button>
          );
        })}
      </div>
    </div>
  ):null;

  const SizeSelector = () => sizeIds.length>0 ? (
    <div style={{ marginBottom:20 }}>
      <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.16em", color:T.muted, marginBottom:10 }}>
        SIZE: <span style={{ fontWeight:500, color:T.ink }}>{selSize?getSizeName(selSize):"—"}</span>
      </div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
        {sizeIds.map(sId=>(
          <button key={sId} className={`sz${selSize===sId?" on":""}`} onClick={()=>setSelSize(sId)}>{getSizeName(sId)}</button>
        ))}
      </div>
    </div>
  ):null;

  /* ── specs table ── */
  const SpecsTable = () => electronicsSpecs.length>0 ? (
    <div style={{ border:`1px solid ${T.border}`, borderRadius:12, overflow:"hidden", marginBottom:20 }}>
      {electronicsSpecs.map(({group,values},i)=>(
        <div key={group.id} style={{ display:"flex", padding:"10px 14px", borderTop:i>0?`1px solid ${T.border}`:"none", background:i%2===0?T.bg:T.bgSoft }}>
          <span style={{ width:140, fontSize:11, color:T.muted, flexShrink:0, fontWeight:600 }}>{group.name}</span>
          <span style={{ fontSize:11, color:T.ink }}>{values.map(v=>v.name).join(", ")}</span>
        </div>
      ))}
      {(product as any)?.description&&(
        <div style={{ padding:"12px 14px", borderTop:`1px solid ${T.border}`, background:T.bgSoft }}>
          <span style={{ fontSize:11, color:T.muted, display:"block", marginBottom:6, fontWeight:600 }}>Description</span>
          <span style={{ fontSize:12, color:T.blackSoft, lineHeight:1.8 }}>{(product as any).description}</span>
        </div>
      )}
    </div>
  ):(product as any)?.description?(
    <div style={{ border:`1px solid ${T.border}`, borderRadius:12, padding:"12px 14px", marginBottom:20, background:T.bgSoft }}>
      <span style={{ fontSize:11, color:T.muted, display:"block", marginBottom:6, fontWeight:600 }}>Description</span>
      <span style={{ fontSize:12, color:T.blackSoft, lineHeight:1.8 }}>{(product as any).description}</span>
    </div>
  ):null;

  /* ── trust strip items ── */
  const trust = [
    { icon:<Truck size={14}/>,     title:"Free delivery over €300",   sub:"Estimated 3–7 business days" },
    { icon:<RotateCcw size={14}/>, title:"30-day free returns",        sub:"Hassle-free return policy"   },
    { icon:<Shield size={14}/>,    title:"Verified listing",           sub:"Every listing is reviewed"   },
  ];

  /* ════════════════ MOBILE LAYOUT ════════════════ */
  const MobileLayout = () => (
    <div style={{ paddingBottom:100 }}>
      {/* image carousel */}
      <div style={{ position:"relative", background:T.bgSoft, aspectRatio:"4/5", overflow:"hidden" }}>
        <AnimatePresence mode="wait">
          <motion.img key={imgIndex} src={photos[imgIndex]} alt={product.name}
            initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            style={{ width:"100%", height:"100%", objectFit:"contain", padding:16 }}/>
        </AnimatePresence>

        {photos.length>1&&(<>
          <button onClick={()=>setImgIndex(i=>(i-1+photos.length)%photos.length)}
            style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", background:"rgba(255,255,255,.9)", border:`1px solid ${T.border}`, width:32, height:32, borderRadius:"50%", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(106,0,255,.1)" }}>
            <ChevronLeft size={16} color={T.ink}/>
          </button>
          <button onClick={()=>setImgIndex(i=>(i+1)%photos.length)}
            style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"rgba(255,255,255,.9)", border:`1px solid ${T.border}`, width:32, height:32, borderRadius:"50%", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(106,0,255,.1)" }}>
            <ChevronRight size={16} color={T.ink}/>
          </button>
        </>)}

        {/* dots */}
        <div style={{ position:"absolute", bottom:10, left:0, right:0, display:"flex", justifyContent:"center", gap:5 }}>
          {photos.map((_,i)=>(
            <div key={i} onClick={()=>setImgIndex(i)}
              style={{ width:i===imgIndex?16:6, height:6, borderRadius:3, background:i===imgIndex?T.purple:"rgba(106,0,255,.2)", cursor:"pointer", transition:"all .2s" }}/>
          ))}
        </div>

        {discount&&<div style={{ position:"absolute", top:12, left:12, background:T.red, color:"#fff", fontSize:11, fontWeight:800, padding:"3px 9px", borderRadius:999 }}>-{discount}%</div>}
        <button onClick={()=>setWished(v=>!v)}
          style={{ position:"absolute", top:12, right:12, background:"rgba(255,255,255,.9)", border:`1px solid ${T.border}`, width:34, height:34, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
          <Heart size={16} color={wished?T.red:T.muted} fill={wished?T.red:"none"}/>
        </button>
      </div>

      {/* info block */}
      <div style={{ padding:"18px 16px 0" }}>
        <div style={{ fontSize:20, fontWeight:800, color:T.ink, lineHeight:1.3, marginBottom:8, letterSpacing:-0.3 }}>{product.name}</div>
        <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:12 }}>
          <span style={{ fontSize:24, fontWeight:900, background:GRAD, backgroundSize:"200% 100%", animation:"gradSpin 3s ease infinite", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>{price}</span>
          {origPrice&&<span style={{ fontSize:14, color:T.muted, textDecoration:"line-through" }}>{origPrice}</span>}
        </div>
        <ColourSelector/>
        <SizeSelector/>
        {/* qty */}
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18 }}>
          <div style={{ fontSize:10, fontWeight:800, letterSpacing:"0.14em", color:T.muted }}>QTY</div>
          <div style={{ display:"flex", border:`1.5px solid ${T.border}`, borderRadius:999, overflow:"hidden" }}>
            <button onClick={()=>setQty(q=>Math.max(1,q-1))} style={{ width:36,height:36,border:"none",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.purple }}><Minus size={13}/></button>
            <div style={{ width:40,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:800,color:T.ink,borderLeft:`1px solid ${T.border}`,borderRight:`1px solid ${T.border}` }}>{qty}</div>
            <button onClick={()=>setQty(q=>q+1)} style={{ width:36,height:36,border:"none",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.purple }}><Plus size={13}/></button>
          </div>
        </div>
      </div>

      <div style={{ margin:"0 16px 16px" }}><SpecsTable/></div>

      {/* trust */}
      <div style={{ margin:"0 16px 16px" }}>
        {trust.map((item,i)=>(
          <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"10px 0", borderBottom:`1px solid ${T.border}` }}>
            <span style={{ color:T.purple, marginTop:1 }}>{item.icon}</span>
            <div>
              <div style={{ fontSize:12, color:T.ink, fontWeight:600 }}>{item.title}</div>
              <div style={{ fontSize:10, color:T.muted, marginTop:1 }}>{item.sub}</div>
            </div>
          </div>
        ))}
      </div>

      <ReviewsSection mob/>
{/*
      <BrandSellerSection mob/>

*/}

      {/* sticky CTA */}
      <div style={{ position:"fixed", bottom:0, left:0, right:0, background:"rgba(255,255,255,0.97)", borderTop:`1px solid ${T.border}`, padding:"12px 16px", display:"flex", gap:10, zIndex:200, backdropFilter:"blur(10px)" }}>
        <button className="ab outline" onClick={handleAddToCart} disabled={!canBuy}>
          <ShoppingCart size={13}/> ADD TO CART
        </button>
        <button className="ab fill" onClick={handleBuyNow} disabled={!canBuy}>
          BUY NOW
        </button>
      </div>
    </div>
  );

  /* ════════════════ DESKTOP LAYOUT ════════════════ */
  const DesktopLayout = () => (
    <div style={{ maxWidth:1280, margin:"0 auto", padding:"28px 40px 60px" }}>
      {/* breadcrumb */}
      <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:20, fontSize:11, color:T.muted }}>
        <span style={{ cursor:"pointer" }} onClick={()=>navigate(`/ProductsHome/${userId}`)}>Home</span>
        <ChevronRight size={11}/>
        <span style={{ cursor:"pointer" }} onClick={()=>navigate(`/ProductsHome/${userId}`)}>Electronics</span>
        <ChevronRight size={11}/>
        <span style={{ color:T.ink }}>{product.name}</span>
      </div>

      {/* 2-col grid */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:52, marginBottom:52 }}>
        {/* left: images */}
        <div>
          <div style={{ display:"flex", gap:12 }}>
            {/* thumb strip */}
            <div style={{ display:"flex", flexDirection:"column", gap:8, width:72, flexShrink:0 }}>
              {photos.map((src,i)=>(
                <div key={i} className={`th${i===imgIndex?" on":""}`}
                  onClick={()=>setImgIndex(i)}
                  style={{ width:72, height:88, background:T.bgSoft }}>
                  <img src={src} alt="" style={{ width:"100%", height:"100%", objectFit:"contain", padding:6 }}/>
                </div>
              ))}
            </div>
            {/* main image */}
            <div style={{ flex:1, position:"relative", background:T.bgSoft, overflow:"hidden", borderRadius:16, border:`1px solid ${T.border}` }}>
              <AnimatePresence mode="wait">
                <motion.img key={imgIndex} src={photos[imgIndex]} alt={product.name}
                  initial={{opacity:0,scale:1.02}} animate={{opacity:1,scale:1}} exit={{opacity:0}}
                  transition={{duration:.3}}
                  style={{ width:"100%", aspectRatio:"4/5", objectFit:"contain", display:"block", padding:24 }}/>
              </AnimatePresence>
              {discount&&<div style={{ position:"absolute", top:14, left:14, background:T.red, color:"#fff", fontSize:11, fontWeight:800, padding:"4px 10px", borderRadius:999 }}>-{discount}%</div>}
              <button onClick={()=>setWished(v=>!v)}
                style={{ position:"absolute", top:14, right:14, background:"rgba(255,255,255,.9)", border:`1px solid ${T.border}`, width:38, height:38, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
                <Heart size={16} color={wished?T.red:T.muted} fill={wished?T.red:"none"}/>
              </button>
            </div>
          </div>
        </div>

        {/* right: info */}
        <div>
          <h1 style={{ fontSize:26, fontWeight:900, color:T.ink, lineHeight:1.2, marginBottom:14, letterSpacing:-0.3 }}>{product.name}</h1>

          <div style={{ display:"flex", alignItems:"baseline", gap:10, marginBottom:22 }}>
            <span style={{ fontSize:30, fontWeight:900, background:GRAD, backgroundSize:"200% 100%", animation:"gradSpin 3s ease infinite", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>{price}</span>
            {origPrice&&<span style={{ fontSize:16, color:T.muted, textDecoration:"line-through" }}>{origPrice}</span>}
            {discount&&<span style={{ fontSize:12, fontWeight:800, color:T.red }}>-{discount}%</span>}
          </div>

          <ColourSelector/>
          <SizeSelector/>

          {/* qty */}
          <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:22 }}>
            <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.14em", color:T.muted }}>QTY</span>
            <div style={{ display:"flex", border:`1.5px solid ${T.border}`, borderRadius:999, overflow:"hidden" }}>
              <button onClick={()=>setQty(q=>Math.max(1,q-1))} style={{ width:38,height:40,border:"none",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.purple }}><Minus size={14}/></button>
              <div style={{ width:44,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:800,color:T.ink,borderLeft:`1px solid ${T.border}`,borderRight:`1px solid ${T.border}` }}>{qty}</div>
              <button onClick={()=>setQty(q=>q+1)} style={{ width:38,height:40,border:"none",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.purple }}><Plus size={14}/></button>
            </div>
          </div>

          {/* CTAs */}
          <div style={{ display:"flex", gap:10, marginBottom:26 }}>
            <button className="ab outline" onClick={handleAddToCart} disabled={!canBuy}><ShoppingCart size={14}/> ADD TO CART</button>
            <button className="ab fill" onClick={handleBuyNow} disabled={!canBuy}>BUY NOW</button>
          </div>

          {/* trust strip */}
          <div style={{ border:`1px solid ${T.border}`, borderRadius:14, overflow:"hidden", marginBottom:22 }}>
            {trust.map((item,i)=>(
              <div key={i} style={{ display:"flex", gap:12, alignItems:"flex-start", padding:"12px 16px", borderTop:i>0?`1px solid ${T.border}`:"none", background:i%2===0?T.bg:T.bgSoft }}>
                <span style={{ color:T.purple, marginTop:1 }}>{item.icon}</span>
                <div>
                  <div style={{ fontSize:12, color:T.ink, fontWeight:600 }}>{item.title}</div>
                  <div style={{ fontSize:10, color:T.muted, marginTop:1 }}>{item.sub}</div>
                </div>
              </div>
            ))}
          </div>

          <SpecsTable/>
        </div>
      </div>

      <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:32 }}>
        <ReviewsSection/>
{/*
        <BrandSellerSection/>


*/}
      </div>
    </div>
  );

  /* ── lightbox ── */
  const LightboxModal = () => (
    <AnimatePresence>
      {modalIndex!==null&&media[modalIndex]?.type==="image"&&(
        <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
          onClick={()=>setModalIndex(null)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.88)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div onClick={e=>e.stopPropagation()} style={{ position:"relative", maxWidth:"92vw", maxHeight:"92vh" }}>
            <img src={media[modalIndex].url} style={{ maxWidth:"100%", maxHeight:"90vh", borderRadius:16, objectFit:"contain" }}/>
            <button onClick={()=>setModalIndex(null)} style={{ position:"absolute", top:-46, right:0, background:"transparent", border:"none", color:"#fff", cursor:"pointer" }}><X size={32}/></button>
            {modalIndex>0&&<button onClick={()=>setModalIndex(i=>i!==null?i-1:i)} style={{ position:"absolute", left:-60, top:"50%", transform:"translateY(-50%)", background:"transparent", border:"none", color:"#fff", cursor:"pointer" }}><ChevronLeft size={48}/></button>}
            {modalIndex<media.length-1&&<button onClick={()=>setModalIndex(i=>i!==null?i+1:i)} style={{ position:"absolute", right:-60, top:"50%", transform:"translateY(-50%)", background:"transparent", border:"none", color:"#fff", cursor:"pointer" }}><ChevronRight size={48}/></button>}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  /* ════════════ ROOT RENDER ════════════ */
  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{CSS}</style>

      {/* back nav */}
      <div style={{ padding:isMobile?"12px 16px":"14px 40px", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", gap:6, background:"#fff", position:"sticky", top:0, zIndex:100, boxShadow:"0 1px 12px rgba(106,0,255,.06)" }}>
        <div style={{ position:"absolute", top:0, left:0, right:0, height:2, background:GA, backgroundSize:"300% 100%", animation:"gradSpin 4s linear infinite" }}/>
        <button onClick={()=>navigate(-1)} style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:5, fontSize:11, color:T.muted }}>
          <ChevronLeft size={14}/> Back
        </button>
      </div>

      {isMobile ? <MobileLayout/> : <DesktopLayout/>}

      {/* ── You Might Also Like ── */}
      {suggestedProducts.length>0&&(
        <div style={{ maxWidth:1280, margin:"0 auto", padding:isMobile?"24px 14px 16px":"32px 56px 24px", borderTop:`1px solid ${T.border}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:24 }}>
            <div style={{ height:1, flex:1, background:T.border }}/>
            <h3 style={{ fontSize:isMobile?18:22, fontWeight:800, color:T.ink, letterSpacing:-0.3, whiteSpace:"nowrap" }}>You Might Also Like</h3>
            <div style={{ height:1, flex:1, background:T.border }}/>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(5,1fr)", gap:isMobile?8:12 }}>
            {suggestedProducts.map((prod:any)=>(
              <div key={prod.listingId} className="ym-card" onClick={()=>{ setActiveId(prod.listingId); window.scrollTo({top:0,behavior:"smooth"}); }}>
                <div style={{ position:"relative", aspectRatio:"3/4", background:T.bgMuted, overflow:"hidden" }}>
                  {prod.photos?.[0]
                    ?<img src={prod.photos[0]} alt={prod.name} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                    :<div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center" }}><Package size={24} color={T.mutedSoft}/></div>
                  }
                  <motion.button whileTap={{scale:.86}}
                    onClick={e=>{ e.stopPropagation(); dispatch(addToCart({ listingId:prod.listingId, name:prod.name??"", price:prod.rate??0, qty:1, photo:prod.photos?.[0], hostId:prod.hostId, brandId:prod.brandId })); }}
                    style={{ position:"absolute", bottom:8, right:8, width:32, height:32, borderRadius:"50%", background:"#fff", border:`1.5px solid ${T.border}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(106,0,255,.12)" }}>
                    <Plus size={13} color={T.purple}/>
                  </motion.button>
                </div>
                <div style={{ padding:"8px 10px 12px" }}>
                  <div style={{ fontSize:13, fontWeight:700, color:T.ink, lineHeight:1.35, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" as any, overflow:"hidden", marginBottom:5 }}>{prod.name}</div>
                  <span style={{ fontSize:13, fontWeight:800, color:T.purple }}>{sym}{((prod.rate??0)*fx).toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* floating cart badge (mobile) */}
      {isMobile&&totalQty>0&&(
        <motion.div initial={{scale:0}} animate={{scale:1}}
          onClick={()=>navigate(`/ProductCheckout/${userId}`,{state:{userId,totalQty,totalPrice,items:cartItems}})}
          style={{ position:"fixed", bottom:88, right:20, width:54, height:54, borderRadius:"50%", background:GRAD, backgroundSize:"200% 100%", animation:"gradSpin 3s ease infinite", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", boxShadow:"0 10px 30px rgba(106,0,255,.4)", zIndex:150 }}>
          <ShoppingCart size={22} color="#fff"/>
          <span style={{ position:"absolute", top:-4, right:-4, background:T.red, color:"#fff", fontSize:9, fontWeight:900, width:18, height:18, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>{totalQty}</span>
        </motion.div>
      )}

      <LightboxModal/>
    </div>
  );
};

export default ProductDetails;